/**
 * Utility functions for Profit and Loss Report
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Generate MM/YYYY labels for every month in an inclusive range.
 */
export const generateMonthLabelsInRange = (startDate, endDate) => {
  const months = [];
  let cursor = dayjs.utc(startDate).startOf('month');
  const endMonth = dayjs.utc(endDate).startOf('month');

  while (cursor.isBefore(endMonth) || cursor.isSame(endMonth, 'month')) {
    months.push(cursor.format('MM/YYYY'));
    cursor = cursor.add(1, 'month');
  }

  return months;
};

export const MONTH_TRANSLATION_KEYS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Build per-year month availability from an inclusive report range.
 */
export const buildYearMonthRangeGroups = (startDate, endDate) => {
  const groups = new Map();
  let cursor = dayjs.utc(startDate).startOf('month');
  const endMonth = dayjs.utc(endDate).startOf('month');

  while (cursor.isBefore(endMonth) || cursor.isSame(endMonth, 'month')) {
    const year = cursor.year();
    const month = cursor.month() + 1;
    if (!groups.has(year)) {
      groups.set(year, []);
    }
    groups.get(year).push(month);
    cursor = cursor.add(1, 'month');
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, months]) => {
      const sorted = [...months].sort((a, b) => a - b);
      return {
        year,
        minMonth: sorted[0],
        maxMonth: sorted[sorted.length - 1],
        availableMonths: sorted,
      };
    });
};

export const buildDefaultYearMonthSelections = (yearGroups) =>
  yearGroups.reduce((acc, group) => {
    acc[group.year] = {
      fromMonth: group.minMonth,
      toMonth: group.maxMonth,
    };
    return acc;
  }, {});

export const expandYearMonthSelections = (yearGroups, selections) => {
  const result = [];

  yearGroups.forEach((group) => {
    const selection = selections[group.year];
    if (!selection) return;

    const fromMonth = Math.min(selection.fromMonth, selection.toMonth);
    const toMonth = Math.max(selection.fromMonth, selection.toMonth);

    for (let month = fromMonth; month <= toMonth; month += 1) {
      if (group.availableMonths.includes(month)) {
        result.push({ year: group.year, month });
      }
    }
  });

  result.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));
  return result;
};

export const validateYearMonthSelections = (yearGroups, selections, t) => {
  if (!yearGroups.length) {
    return { isValid: false, message: t('SelectAtLeastOneMonth') };
  }

  for (const group of yearGroups) {
    const selection = selections[group.year];
    if (!selection) {
      return { isValid: false, message: t('InvalidYearMonthRange') };
    }

    const { fromMonth, toMonth } = selection;
    if (!fromMonth || !toMonth) {
      return { isValid: false, message: t('InvalidYearMonthRange') };
    }

    if (fromMonth > toMonth) {
      return { isValid: false, message: t('InvalidYearMonthRange') };
    }

    if (!group.availableMonths.includes(fromMonth) || !group.availableMonths.includes(toMonth)) {
      return { isValid: false, message: t('InvalidYearMonthRange') };
    }
  }

  const expanded = expandYearMonthSelections(yearGroups, selections);
  if (!expanded.length) {
    return { isValid: false, message: t('SelectAtLeastOneMonth') };
  }

  return { isValid: true, message: '', selectedMonths: expanded };
};

/** All months in an inclusive date range, for detailed monthly HTML/PDF payloads. */
export const buildSelectedMonthsFromDateRange = (startDate, endDate) => {
  const yearGroups = buildYearMonthRangeGroups(startDate, endDate);
  const selections = buildDefaultYearMonthSelections(yearGroups);
  return expandYearMonthSelections(yearGroups, selections);
};

export const selectedMonthsToDateBounds = (selectedMonths) => {
  if (!selectedMonths?.length) return null;

  const first = selectedMonths[0];
  const last = selectedMonths[selectedMonths.length - 1];

  return {
    startDate: dayjs.utc(`${first.year}-${String(first.month).padStart(2, '0')}-01`).startOf('month').toDate(),
    endDate: dayjs.utc(`${last.year}-${String(last.month).padStart(2, '0')}-01`).endOf('month').toDate(),
  };
};

/**
 * Group an array by a given key
 */
export const groupBy = (arr, key) => {
  return arr.reduce((acc, item) => {
    if (!acc[item[key]]) {
      acc[item[key]] = [];
    }
    acc[item[key]].push(item);
    return acc;
  }, {});
};

/**
 * Format date range for display
 */
export const formatDateRange = (startDate, endDate, prefix = 'לתקופה') => {
  if (!startDate || !endDate) return '';
  
  // Extract month and year from the date strings
  // Assuming dates are in format like "1/2024" or similar
  const startParts = startDate.toString().split('/');
  const endParts = endDate.toString().split('/');
  
  if (startParts.length >= 2 && endParts.length >= 2) {
    const startMonth = startParts[0];
    const endMonth = endParts[0];
    const year = endParts[1] || startParts[1];
    
    if (startMonth === endMonth) {
      return `${prefix} ${startMonth}/${year}`;
    } else {
      return `${prefix} ${startMonth}-${endMonth}/${year}`;
    }
  }
  
  // Fallback to original format if parsing fails
  return `${prefix} ${startDate}-${endDate}`;
};

/**
 * Generate comprehensive transaction mapping for period comparison
 */
export const generateTransactionMapping = (firstPeriodTransactions, secondPeriodTransactions) => {
  let allUniqueTransactions = new Map();
  
  // Add all transactions from first period
  firstPeriodTransactions.forEach(tr => {
    const key = `${tr.accountCardId}_${tr.palCode}_${tr.palCodeCategory}_${tr.palCodeAccountGroup}`;
    allUniqueTransactions.set(key, {
      firstPeriod: tr,
      secondPeriod: null,
      // Use first period transaction as the base for display properties
      accountCardId: tr.accountCardId,
      accountCardName: tr.accountCardName,
      palCode: tr.palCode,
      palCodeCategory: tr.palCodeCategory,
      palCodeAccountGroup: tr.palCodeAccountGroup,
      accountingGroupClassification: tr.accountingGroupClassification,
      palCodeType: tr.palCodeType
    });
  });

  // Add transactions from second period, matching with existing or creating new entries
  if (secondPeriodTransactions) {
    secondPeriodTransactions.forEach(tr => {
      const key = `${tr.accountCardId}_${tr.palCode}_${tr.palCodeCategory}_${tr.palCodeAccountGroup}`;
      if (allUniqueTransactions.has(key)) {
        // Update existing entry with second period data
        allUniqueTransactions.get(key).secondPeriod = tr;
      } else {
        // Create new entry for transaction that only exists in second period
        allUniqueTransactions.set(key, {
          firstPeriod: null,
          secondPeriod: tr,
          // Use second period transaction as the base for display properties
          accountCardId: tr.accountCardId,
          accountCardName: tr.accountCardName,
          palCode: tr.palCode,
          palCodeCategory: tr.palCodeCategory,
          palCodeAccountGroup: tr.palCodeAccountGroup,
          accountingGroupClassification: tr.accountingGroupClassification,
          palCodeType: tr.palCodeType
        });
      }
    });
  }

  return allUniqueTransactions;
};

/**
 * Create synthetic transaction with zero values for missing data
 */
export const createSyntheticTransaction = (transactionPair, isFirstPeriod) => {
  const baseTr = isFirstPeriod ? transactionPair.firstPeriod : transactionPair.secondPeriod;
  const fallbackTr = transactionPair.firstPeriod || transactionPair.secondPeriod;
  
  if (baseTr) {
    return baseTr;
  }
  
  return {
    accountCardId: fallbackTr.accountCardId,
    accountCardName: fallbackTr.accountCardName,
    palCode: fallbackTr.palCode,
    palCodeCategory: fallbackTr.palCodeCategory,
    palCodeAccountGroup: fallbackTr.palCodeAccountGroup,
    accountingGroupClassification: fallbackTr.accountingGroupClassification,
    palCodeType: fallbackTr.palCodeType,
    initDebitAccount: 0,
    initCreditAccount: 0,
    debitAccount: 0,
    creditAccount: 0,
    balance: 0
  };
};

/**
 * Filter transactions based on zero-balance setting
 */
export const filterTransactionsByZeroBalance = (
  transactionPairs, 
  showAccountCardsWithZeros, 
  secondPeriodData
) => {
  let filteredTransactions = [];
  let comparisonTransactions = [];
  
  transactionPairs.forEach((transactionPair, key) => {
    const firstTr = transactionPair.firstPeriod;
    const secondTr = transactionPair.secondPeriod;
    
    // Create synthetic transactions
    const syntheticFirstTr = createSyntheticTransaction(transactionPair, true);
    const syntheticSecondTr = createSyntheticTransaction(transactionPair, false);
    
    // Apply zero-balance filtering if enabled
    if (!showAccountCardsWithZeros) {
      const hasNonZeroInFirstPeriod = firstTr && (
        firstTr.initDebitAccount !== 0 ||
        firstTr.initCreditAccount !== 0 ||
        firstTr.debitAccount !== 0 ||
        firstTr.creditAccount !== 0 ||
        firstTr.balance !== 0
      );
      
      const hasNonZeroInSecondPeriod = secondTr && (
        secondTr.initDebitAccount !== 0 ||
        secondTr.initCreditAccount !== 0 ||
        secondTr.debitAccount !== 0 ||
        secondTr.creditAccount !== 0 ||
        secondTr.balance !== 0
      );
      
      // Only include if there's non-zero activity in at least one period
      if (hasNonZeroInFirstPeriod || hasNonZeroInSecondPeriod) {
        filteredTransactions.push(syntheticFirstTr);
        if (secondPeriodData) {
          comparisonTransactions.push(syntheticSecondTr);
        }
      }
    } else {
      // Include all transactions
      filteredTransactions.push(syntheticFirstTr);
      if (secondPeriodData) {
        comparisonTransactions.push(syntheticSecondTr);
      }
    }
  });

  return { filteredTransactions, comparisonTransactions };
};

/**
 * Calculate total income from transactions
 * Total income is the sum of only POSITIVE balances where palCodeAccountGroup === "הכנסות"
 * This represents gross revenue (before cost of sales deductions) for percentage calculations
 * Using only positive balances ensures percentages don't exceed 100% for gross sales items
 */
export const calculateTotalIncome = (transactions) => {
  return transactions.reduce((total, transaction) => {
    if (transaction.palCodeAccountGroup === 'הכנסות' && transaction.balance > 0) {
      return total + transaction.balance;
    }
    return total;
  }, 0);
};

/**
 * Generate unique categories and codes for display
 */
export const generateUniqueCategories = (transactions) => {
  const uniquePalCodeCategories = [
    ...new Set(
      transactions
        .map(tr => tr.palCodeCategory)
        .filter(category => category !== "NonPalCodeCategory")
    ),
  ];

  const uniquePalCodes = [
    ...new Set(
      transactions
        .map(tr => tr.palCode)
        .filter(code => code !== "NonPalCode")
    ),
  ];

  return { uniquePalCodeCategories, uniquePalCodes };
};

/** Tax-exempt businesses ("עוסק פטור") use "ע.פ."; all others use "ח.פ./ע.מ." */
export const getCaseNumberLabel = (caseType) =>
  caseType === 'עוסק פטור' ? 'ע.פ.' : 'ח.פ./ע.מ.';
